import { useDeferredValue, useMemo, useState, useEffect } from "react";
import {
  Plus,
  Search,
  ChevronRight,
  Star,
  Flag,
  Volume2,
  Trash2,
  SquarePen,
  ArrowLeft,
  Check,
} from "lucide-react";
import {
  activeReviews,
  dateLabel,
  examplesFor,
  modeLabels,
  ratingLabels,
  type Snapshot,
  type WordSense,
} from "./domain";
import { db, deleteWord } from "./db";
import { analytics } from "./analytics";
import { difficultWords } from "./study";
import { retrievability } from "./scheduler";
import { speak } from "./platform";
import { starterRows, copyStarter } from "./content";
import { importVocabulary } from "./transfer";
import { createCollection } from "./Editor";
import { Dialog, Empty, IconButton, Panel, Stat } from "./components";

export type LibraryFilter =
  "all" | "favorite" | "difficult" | "learned" | "due" | "new";
export default function Library({
  s,
  onAdd,
  onEdit,
  onDetail,
  initialFilter = "all",
  notify,
}: {
  s: Snapshot;
  onAdd: () => void;
  onEdit: (w: WordSense) => void;
  onDetail: (w: WordSense) => void;
  initialFilter?: LibraryFilter;
  notify: (m: string) => void;
}) {
  const [search, setSearch] = useState(""),
    [filter, setFilter] = useState<LibraryFilter>(initialFilter),
    [deck, setDeck] = useState("all"),
    [page, setPage] = useState(1),
    [starter, setStarter] = useState(false),
    [collection, setCollection] = useState(false),
    [name, setName] = useState(""),
    [error, setError] = useState("");
  const query = useDeferredValue(search.trim().toLocaleLowerCase("tr-TR"));
  const hard = useMemo(() => difficultWords(s), [s]),
    a = useMemo(() => analytics(s), [s]);
  const indexes = useMemo(() => {
    const cardMap = new Map(s.cards.map((c) => [c.id, c]));
    const dueIds = new Set(a.due.map((st) => cardMap.get(st.cardId)?.wordId));
    const learned = new Set(
      s.states
        .filter((st) => st.memory.state === 2)
        .map((st) => cardMap.get(st.cardId)?.wordId),
    );
    const introduced = new Set(activeReviews(s.events).map((e) => e.wordId));
    const currentIds = new Map(
      s.words.map((w) => [w.id, w.exampleIds ? new Set(w.exampleIds) : null]),
    );
    const sentences = new Map<string, string[]>();
    for (const e of s.examples) {
      if (!currentIds.get(e.wordId) || currentIds.get(e.wordId)!.has(e.id)) {
        const list = sentences.get(e.wordId) ?? [];
        list.push(e.en, e.tr);
        sentences.set(e.wordId, list);
      }
    }
    const searchable = new Map(
      s.words.map((w) => [
        w.id,
        [w.lemma, w.meaning, w.sense, w.tags.join(" "), ...(sentences.get(w.id) ?? [])]
          .join(" ")
          .toLocaleLowerCase("tr-TR"),
      ]),
    );
    return { dueIds, learned, introduced, searchable };
  }, [s, a]);
  const words = useMemo(
    () =>
      s.words
        .filter(
          (w) =>
            !w.deleted &&
            w.active &&
            (deck === "all" || w.deckIds.includes(deck)) &&
            (filter === "all" ||
              (filter === "favorite" && w.favorite) ||
              (filter === "difficult" && hard.has(w.id)) ||
              (filter === "learned" && indexes.learned.has(w.id)) ||
              (filter === "due" && indexes.dueIds.has(w.id)) ||
              (filter === "new" && !indexes.introduced.has(w.id))) &&
            (!query || indexes.searchable.get(w.id)?.includes(query)),
        )
        .sort(
          (x, y) =>
            y.createdAt.localeCompare(x.createdAt) ||
            x.lemma.localeCompare(y.lemma, "en"),
        ),
    [s, deck, filter, query, indexes, hard],
  );
  useEffect(() => setPage(1), [query, deck, filter]);
  return (
    <div className="stack">
      <div className="page-heading heading-actions">
        <div>
          <h1 tabIndex={-1}>Kelimelerim</h1>
          <p>
            {a.uniqueWords} kelime · {a.uniquePhrases} kalıp
          </p>
        </div>
        <button className="primary" onClick={onAdd}>
          <Plus size={20} />
          Kelime ekle
        </button>
      </div>
      <div className="library-tools">
        <label className="search-box">
          <Search size={19} aria-hidden="true" />
          <span className="sr-only">Kelime, anlam veya cümle ara</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kelime, anlam veya cümle ara"
          />
        </label>
        <label>
          <span className="sr-only">Koleksiyon seç</span>
          <select value={deck} onChange={(e) => setDeck(e.target.value)}>
            <option value="all">Tüm koleksiyonlar</option>
            {s.decks.map((d) => (
              <option value={d.id} key={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="filter-chips" aria-label="Kelime filtresi">
        {(
          [
            ["all", "Tümü"],
            ["favorite", "Favoriler"],
            ["difficult", "Zor kelimeler"],
            ["learned", "Tekrar aşamasında"],
            ["due", "Tekrar bekleyen"],
            ["new", "Yeni"],
          ] as const
        ).map(([v, label]) => (
          <button key={v} aria-pressed={filter === v} onClick={() => setFilter(v)}>
            {label}
          </button>
        ))}
      </div>
      <div className="section-heading">
        <p className="muted" role="status">
          {words.length} anlam kaydı
        </p>
        <button className="text-button" onClick={() => setCollection(true)}>
          <Plus size={16} />
          Koleksiyon oluştur
        </button>
      </div>
      {words.length ? (
        <>
          <ul className="word-list">
            {words.slice((page - 1) * 50, page * 50).map((w) => {
              const ex = examplesFor(s, w.id)[0];
              return (
                <li key={w.id}>
                  <button className="word-row" onClick={() => onDetail(w)}>
                    <span className="word-initial" aria-hidden="true" lang="en">
                      {w.lemma.slice(0, 1)}
                    </span>
                    <span className="word-main">
                      <strong lang="en">
                        {w.lemma}
                        {w.favorite && <Star size={14} aria-label="Favori" />}
                        {hard.has(w.id) && <Flag size={14} aria-label="Zor kelime" />}
                      </strong>
                      <span>{w.meaning}</span>
                      {ex && <small lang="en">{ex.en}</small>}
                    </span>
                    <span className="word-meta">
                      <span className="badge">{w.cefr}</span>
                      <ChevronRight size={18} aria-hidden="true" />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {words.length > 50 && (
            <div className="pagination">
              <button
                className="secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Önceki
              </button>
              <span>
                {page} / {Math.ceil(words.length / 50)}
              </span>
              <button
                className="secondary"
                disabled={page >= Math.ceil(words.length / 50)}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </button>
            </div>
          )}
        </>
      ) : (
        <Empty
          title={
            query || filter !== "all" || deck !== "all"
              ? "Bu filtrede kelime yok"
              : "İlk kelimenle başla"
          }
          action={
            <button
              className="secondary"
              onClick={
                query || filter !== "all" || deck !== "all"
                  ? () => {
                      setSearch("");
                      setFilter("all");
                      setDeck("all");
                    }
                  : onAdd
              }
            >
              {query || filter !== "all" || deck !== "all"
                ? "Filtreleri temizle"
                : "Kendi kelimemi ekle"}
            </button>
          }
        >
          {query
            ? `“${search}” için sonuç bulunamadı.`
            : "Fielse’de karşılaştığın bir kelimeyi, anlamını ve kısa bir cümleyi ekle."}
        </Empty>
      )}
      <section className="starter-banner">
        <div>
          <h2>İstersen örneklerden başla</h2>
          <p>
            {starterRows.length} özgün A1/A2 kelime ve kalıp. Hazır paket eklemek
            zorunlu değil.
          </p>
        </div>
        <button className="secondary" onClick={() => setStarter(true)}>
          Örnek defterini aç
        </button>
      </section>
      {starter && <Starter s={s} onClose={() => setStarter(false)} notify={notify} />}
      {collection && (
        <Dialog title="Koleksiyon oluştur" onClose={() => setCollection(false)}>
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createCollection(name);
                setCollection(false);
                setName("");
                notify("Koleksiyon oluşturuldu.");
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          >
            <label>
              Koleksiyon adı
              <input
                autoFocus
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Fielse notlarım"
              />
            </label>
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            <button className="primary">Koleksiyonu oluştur</button>
          </form>
        </Dialog>
      )}
    </div>
  );
}
function Starter({
  s,
  onClose,
  notify,
}: {
  s: Snapshot;
  onClose: () => void;
  notify: (m: string) => void;
}) {
  const [search, setSearch] = useState(""),
    [selected, setSelected] = useState(new Set<string>()),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const filtered = starterRows.filter((r) =>
    (r.word.lemma + " " + r.word.meaning)
      .toLocaleLowerCase("tr-TR")
      .includes(search.toLocaleLowerCase("tr-TR")),
  );
  async function add() {
    setBusy(true);
    try {
      const r = await importVocabulary(
        starterRows
          .filter((r) => selected.has(r.word.id))
          .map((r) => {
            const copy = copyStarter(r);
            return {
              word: {
                ...copy.word,
                deckIds: r.word.deckIds,
                tags: r.word.tags,
                source: "starter" as const,
              },
              example: copy.example,
            };
          }),
      );
      notify(`${r.added} anlam eklendi; ${r.duplicates} mevcut kayıt korundu.`);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog title="İsteğe bağlı örnek defteri" onClose={onClose} wide>
      <div className="stack">
        <p className="muted">
          Sentence için hazırlanmış örneklerdir. Düzey etiketleri yaklaşık öğretim
          düzeyini gösterir; sözlük onayı değildir. Seçip eklediğinde günlük çalışma
          planına katılır.
        </p>
        <label>
          Örnek ara
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="mistake veya hata"
          />
        </label>
        <div className="toolbar">
          <button
            className="secondary"
            onClick={() => setSelected(new Set(filtered.map((r) => r.word.id)))}
          >
            Görünenleri seç ({filtered.length})
          </button>
          <button className="text-button" onClick={() => setSelected(new Set())}>
            Seçimi temizle
          </button>
        </div>
        <div className="starter-list">
          {filtered.map((r) => (
            <label key={r.word.id} className="starter-row">
              <input
                type="checkbox"
                checked={selected.has(r.word.id)}
                onChange={(e) =>
                  setSelected((old) => {
                    const n = new Set(old);
                    if (e.target.checked) n.add(r.word.id);
                    else n.delete(r.word.id);
                    return n;
                  })
                }
              />
              <span>
                <strong lang="en">{r.word.lemma}</strong>
                <span>{r.word.meaning}</span>
                <small lang="en">{r.example.en}</small>
              </span>
              <span className="badge">{r.word.cefr}</span>
            </label>
          ))}
        </div>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button className="primary" disabled={!selected.size || busy} onClick={add}>
          <Plus size={18} />
          {busy ? "Ekleniyor…" : `${selected.size} seçili anlamı ekle`}
        </button>
      </div>
    </Dialog>
  );
}

export function WordDetail({
  s,
  word,
  onBack,
  onEdit,
  notify,
}: {
  s: Snapshot;
  word: WordSense;
  onBack: () => void;
  onEdit: () => void;
  notify: (m: string) => void;
}) {
  const events = activeReviews(s.events)
      .filter((e) => e.wordId === word.id)
      .sort((a, b) => b.at.localeCompare(a.at)),
    states = s.states.filter((st) =>
      s.cards.some((c) => c.id === st.cardId && c.wordId === word.id),
    ),
    [estimates, setEstimates] = useState<Record<string, number | null>>({}),
    [remove, setRemove] = useState(false),
    [audio, setAudio] = useState("");
  useEffect(() => {
    let active = true;
    Promise.all(
      states.map(async (st) => [st.id, await retrievability(st)] as const),
    ).then((v) => {
      if (active) setEstimates(Object.fromEntries(v));
    });
    return () => {
      active = false;
    };
  }, [s.states, word.id]);
  const success = events.filter((e) => e.effectiveRating > 1).length,
    avg = events.length
      ? events.reduce((n, e) => n + e.answerMs, 0) / events.length / 1000
      : null;
  return (
    <div className="stack">
      <button className="text-button back" onClick={onBack}>
        <ArrowLeft size={17} />
        Kelimelerime dön
      </button>
      <div className="page-heading heading-actions">
        <div>
          <span className="eyebrow">
            {word.cefr} · {word.pos}
          </span>
          <h1 lang="en" tabIndex={-1}>
            {word.lemma}
          </h1>
          <p>{word.meaning}</p>
          <p lang="en" className="muted">
            {word.ipa}
          </p>
        </div>
        <div className="toolbar">
          <IconButton
            label="Kelimeyi dinle"
            onClick={() => speak(word.lemma, s.settings, setAudio)}
          >
            <Volume2 />
          </IconButton>
          <IconButton
            label="Favorilere ekle"
            pressed={word.favorite}
            onClick={() => void db.words.update(word.id, { favorite: !word.favorite })}
          >
            <Star fill={word.favorite ? "currentColor" : "none"} />
          </IconButton>
          <IconButton
            label="Zor kelime olarak işaretle"
            pressed={word.difficult}
            onClick={() =>
              void db.words.update(word.id, { difficult: !word.difficult })
            }
          >
            <Flag fill={word.difficult ? "currentColor" : "none"} />
          </IconButton>
          <button className="secondary" onClick={onEdit}>
            <SquarePen size={17} />
            Düzenle
          </button>
        </div>
      </div>
      <p role="status" className="audio-status">
        {audio}
      </p>
      <div className="stats-grid">
        <Stat label="Toplam çalışma" value={events.length} />
        <Stat
          label="Başarılı / başarısız"
          value={`${success} / ${events.length - success}`}
          detail="FSRS'ye iletilen değerlendirme"
        />
        <Stat
          label="Ortalama cevap"
          value={avg === null ? "—" : avg.toFixed(1).replace(".", ",") + " sn"}
        />
        <Stat label="İpucu kullanılan" value={events.filter((e) => e.hint).length} />
      </div>
      <Panel title="Öğrenme geçmişi">
        <dl className="detail-list">
          <div>
            <dt>Eklenme</dt>
            <dd>{dateLabel(word.createdAt)}</dd>
          </div>
          <div>
            <dt>İlk çalışma</dt>
            <dd>{dateLabel(events.at(-1)?.at ?? null)}</dd>
          </div>
          <div>
            <dt>Son çalışma</dt>
            <dd>{dateLabel(events[0]?.at ?? null)}</dd>
          </div>
          <div>
            <dt>Koleksiyonlar</dt>
            <dd>
              {word.deckIds
                .map((k) => s.decks.find((d) => d.id === k)?.name)
                .join(", ")}
            </dd>
          </div>
        </dl>
      </Panel>
      <Panel title="Çalıştığın cümleler">
        <div className="stack">
          {examplesFor(s, word.id).map((ex) => (
            <div className="example-detail" key={ex.id}>
              <p lang="en">{ex.en}</p>
              <p className="muted">{ex.tr}</p>
            </div>
          ))}
          <button className="text-button" onClick={onEdit}>
            <Plus size={17} />
            Yeni örnek ekle
          </button>
        </div>
      </Panel>
      {(word.collocations.length > 0 || word.commonError) && (
        <Panel title="Birlikte kullanılan kelimeler">
          <p lang="en">{word.collocations.join(" · ")}</p>
          {word.commonError && <p className="muted">{word.commonError}</p>}
        </Panel>
      )}
      <Panel title="Tekrar planı ve hafıza modeli">
        <p className="muted">
          Her yön ayrı hesaplanır. Bu sayılar test sonucu veya kalıcı öğrenme belgesi
          değil, FSRS-7 model tahminidir.
        </p>
        <div className="memory-grid">
          {states.map((st) => {
            const direction = s.cards.find((c) => c.id === st.cardId)?.direction;
            return (
              <div className="memory-card" key={st.id}>
                <h3>
                  {direction === "receptive"
                    ? "İngilizce → Türkçe"
                    : direction === "productive"
                      ? "Türkçe → İngilizce"
                      : "Kalıp testi"}
                </h3>
                <dl className="detail-list">
                  <div>
                    <dt>Durum</dt>
                    <dd>
                      {
                        [
                          "Yeni",
                          "Öğreniliyor",
                          "Tekrar aşamasında",
                          "Yeniden öğreniliyor",
                        ][st.memory.state]
                      }
                    </dd>
                  </div>
                  <div>
                    <dt>Sonraki tekrar</dt>
                    <dd>
                      {st.memory.reps
                        ? dateLabel(st.memory.dueAt)
                        : "İlk çalışmayı bekliyor"}
                    </dd>
                  </div>
                  <div>
                    <dt>Hafıza dayanıklılığı</dt>
                    <dd>{st.memory.stability.toFixed(2).replace(".", ",")} gün</dd>
                  </div>
                  <div>
                    <dt>Zorluk</dt>
                    <dd>{st.memory.difficulty.toFixed(2).replace(".", ",")} / 10</dd>
                  </div>
                  <div>
                    <dt>Tahmini hatırlanabilirlik</dt>
                    <dd>
                      {estimates[st.id] == null
                        ? "—"
                        : Math.round(estimates[st.id]! * 100) + "%"}
                    </dd>
                  </div>
                </dl>
              </div>
            );
          })}
        </div>
        <details>
          <summary>Teknik metrikler ne anlama geliyor?</summary>
          <p>
            Dayanıklılık (stability), modelin unutmanın hızını tanımlayan gün cinsinden
            parametresidir. Daha yüksek değer, genellikle daha yavaş unutmayı temsil
            eder. Zorluk (difficulty), kartın geçmiş cevaplara göre tahmin edilen
            güçlüğüdür. Hatırlanabilirlik (retrievability), son çalışmadan bu yana geçen
            süreye göre modelin bugün için hesapladığı olasılıktır. Kısa süreli ikinci
            dayanıklılık da FSRS-7 durumunda saklanır.
          </p>
        </details>
      </Panel>
      <Panel title="Tekrar geçmişi">
        {events.length ? (
          <div className="history-list">
            {events.map((e) => (
              <details key={e.id}>
                <summary>
                  <span>{dateLabel(e.at)}</span>
                  <span>
                    {ratingLabels[e.rating]}
                    {e.hint ? " · ipucu" : ""}
                  </span>
                </summary>
                <dl className="detail-list">
                  <div>
                    <dt>Mod</dt>
                    <dd>{modeLabels[e.mode]}</dd>
                  </div>
                  <div>
                    <dt>Değerlendirme</dt>
                    <dd>
                      {e.assessment === "automatic"
                        ? "Otomatik hedef eşleşmesi"
                        : "Öz değerlendirme"}
                    </dd>
                  </div>
                  <div>
                    <dt>Yanıt</dt>
                    <dd lang="en">{e.answer || "Zihinden yanıt"}</dd>
                  </div>
                  {e.expected && (
                    <div>
                      <dt>Kayıtlı hedef cevap</dt>
                      <dd lang="en">{e.expected}</dd>
                    </div>
                  )}
                  {e.prompt && (
                    <div>
                      <dt>Gösterilen soru</dt>
                      <dd lang="en">{e.prompt}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Cevap öncesi tahmin</dt>
                    <dd>
                      {
                        {
                          remembered: "Hatırladım",
                          unsure: "Emin değilim",
                          forgot: "Hatırlayamadım",
                          unrecorded: "Kaydedilmedi",
                        }[e.prediction]
                      }
                    </dd>
                  </div>
                  <div>
                    <dt>FSRS değerlendirmesi</dt>
                    <dd>{ratingLabels[e.effectiveRating]}</dd>
                  </div>
                  <div>
                    <dt>Cevap süresi</dt>
                    <dd>{(e.answerMs / 1000).toFixed(1).replace(".", ",")} sn</dd>
                  </div>
                  <div>
                    <dt>Çeviri</dt>
                    <dd>{e.translation ? "Açıldı" : "Açılmadı"}</dd>
                  </div>
                  <div>
                    <dt>Önceki / yeni tekrar</dt>
                    <dd>
                      {dateLabel(e.previousDue)} / {dateLabel(e.nextDue)}
                    </dd>
                  </div>
                  <div>
                    <dt>Çalışılan cümle</dt>
                    <dd lang="en">
                      {s.examples.find((ex) => ex.id === e.exampleId)?.en}
                    </dd>
                  </div>
                </dl>
              </details>
            ))}
          </div>
        ) : (
          <p className="muted">
            Henüz çalışma kaydı yok. İlk değerlendirmenden sonra burada görünecek.
          </p>
        )}
      </Panel>
      {word.notes && (
        <Panel title="Notların">
          <p>{word.notes}</p>
        </Panel>
      )}
      <button className="danger text-button" onClick={() => setRemove(true)}>
        <Trash2 size={17} />
        Bu kelimeyi sil
      </button>
      {remove && (
        <Dialog title={`“${word.lemma}” silinsin mi?`} onClose={() => setRemove(false)}>
          <div className="stack">
            <p>
              Bu anlam ve kartları kütüphaneden ve çalışma planından çıkarılacak. Önceki
              çalışma kayıtları analiz ve yedek bütünlüğü için korunur.
            </p>
            <div className="dialog-actions">
              <button className="secondary" onClick={() => setRemove(false)}>
                Vazgeç
              </button>
              <button
                className="danger-button"
                onClick={async () => {
                  await deleteWord(word.id);
                  notify("Kelime çalışma planından çıkarıldı.");
                  onBack();
                }}
              >
                Kelimeyi sil
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
